import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import TableUI from "../../components/TableUI";
import { ActionButton, Buttons } from "../../components/Buttons";
import { MdOutlineDelete } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import {
  fetchPlans,
  addPlan,
  updatePlan,
  deletePlan,
} from "../../slice/PlanSlice";
import { HiOutlineDotsVertical } from "react-icons/hi";
import PageTitle from "../../components/PageTitle";
import CustomModal from "../../components/Modal";
import NotifyData from "../../components/NotifyData";
import PlanCreation from "../creations/PlanCreation";
import { TextInputform } from "../../components/Forms";
import Pagination from "../../components/Pagination";

// 1. Import Excel libraries
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Plan = () => {
  const dispatch = useDispatch();
  const { plans } = useSelector((state) => state.plans);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;

  const planSchema = [
    { name: "plan_name", label: "Plan Name", type: "text", required: true },
    { name: "plan_price", label: "Price", type: "number", required: true },
  ];

  const [formData, setFormData] = useState({
    plan_name: "",
    plan_price: "",
  });
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleOpen = () => setShow(true);
  const handleClose = () => setShow(false);

  useEffect(() => {
    dispatch(fetchPlans(searchTerm));
  }, [dispatch, searchTerm]);

  // 2. Excel Export Function
  const handleExportExcel = () => {
    if (!plans || plans.length === 0) {
      NotifyData("No data available to export", "error");
      return;
    }

    const exportData = plans.map((plan, index) => ({
      "S.No": index + 1,
      "Plan Name": plan.plan_name,
      "Price (₹)": plan.plan_prize,
      "Customer Count": plan.customer_count || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plans");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, `Plans_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleEdit = (plan) => {
    setEditMode(true);
    setSelectedPlan(plan || {});
    setFormData({
      plan_name: plan?.plan_name || "",
      plan_price: plan?.plan_prize || "",
    });
    handleOpen();
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({ plan_name: "", plan_price: "" });
    handleOpen();
  };

  const handleSubmit = async () => {
    if (!formData.plan_name || !formData.plan_price) {
      NotifyData("Plan Creation Failed: All fields are required", "error");
      return;
    }
if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }    setIsSubmitting(true);
    const planData = {
      plan_name: formData.plan_name,
      plan_price: formData.plan_price,
      current_user_id: currentUserId,
    };

    try {
      const msg = await dispatch(addPlan(planData)).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchPlans(searchTerm));
      handleClose();
      setFormData({ plan_name: "", plan_price: "" });
      setEditMode(false);
    } catch (error) {
      NotifyData(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.plan_name || !formData.plan_price) {
      NotifyData("Plan Update Failed: All fields are required", "error");
      return;
    }
      if (!currentUserId || !selectedPlan?.plan_id) {
      NotifyData("Plan Update Failed: Invalid data", "error");
      return;
    }
    setIsSubmitting(true);
    const planData = {
      plan_name: formData.plan_name,
      plan_price: formData.plan_price,
      current_user_id: currentUserId,
      plan_id: selectedPlan.plan_id,
    };

    try {
      const msg = await dispatch(updatePlan(planData)).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchPlans(searchTerm));
      handleClose();
      setFormData({ plan_name: "", plan_price: "" });
      setEditMode(false);
    } catch (error) {
      NotifyData(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (planId) => {
     if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }

    try {
      const msg = await dispatch(deletePlan({ planId, currentUserId })).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchPlans(searchTerm));
    } catch (error) {
      NotifyData(error.message, "error");
    }
  };

  const filteredPlans =
    plans?.filter((plan) =>
      plan?.plan_name?.toLowerCase()?.includes(searchTerm.toLowerCase())
    ) || [];

  const totalRecords = filteredPlans.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const paginatedPlans = filteredPlans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const RoleHead = ["No", "Plan Name", "Price", "Customer Count"];

  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col xs="12" lg="6" className="py-3">
            <PageTitle PageTitle="Plan" showButton={false} />
          </Col>
          <Col xs="12" lg="6" className="py-3 text-end d-flex justify-content-end gap-2">
             <Buttons
              btnlabel="Add New"
              className="add-btn"
              onClick={handleCreate}
            />
            <Buttons
              btnlabel="Download Excel"
              className="add-btn"
              onClick={handleExportExcel}
            />
           
          </Col>
          <Col xs="12" lg="3" className="py-3">
            <TextInputform
              textlabel="Search Plans"
              PlaceHolder="Search by plan name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>

          <Col xs="12" className="d-flex justify-content-end">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </Col>

          <Col xs="12" className="py-3 tableui">
            <TableUI
              headers={RoleHead}
              body={paginatedPlans.map((plan, index) => ({
                key: plan.plan_id,
                values: [
                  (currentPage - 1) * itemsPerPage + index + 1,
                  plan.plan_name,
                  plan.plan_prize,
                  plan.customer_count || 0,
                  <ActionButton
                    options={[
                      {
                        label: "Edit",
                        icon: <LiaEditSolid />,
                        onClick: () => handleEdit(plan),
                      },
                      {
                        label: "Delete",
                        icon: <MdOutlineDelete />,
                        onClick: () => handleDelete(plan.plan_id),
                      },
                    ]}
                    label={<HiOutlineDotsVertical />}
                  />,
                ],
              }))}
            />
          </Col>
        </Row>
      </Container>

      <CustomModal
        show={show}
        setShow={setShow}
        pageTitle={editMode ? <>Edit Plan</> : <>Create Plan</>}
        showButton={true}
        submitButton={true}
        submitLabel={isSubmitting ? "Processing..." : editMode ? "Update" : "Submit"}
        CancelLabel="Cancel"
        BodyComponent={
          <PlanCreation
            formData={formData}
            setFormData={setFormData}
            schema={planSchema}
          />
        }
        OnClick={editMode ? handleUpdate : handleSubmit}
        Size="md"
        handleOpen={handleOpen}
        handleClose={handleClose}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Plan;